import React, { useState, useEffect, useRef } from "react";
import { parse } from 'svg-parser';
import './App.css';
import { reconstructCanvasState, remapEnvelopeGridColors, resolveSubstituteColorId } from './Utils/canvasUtils';
import TopBar from './Components/TopBar/TopBar';
import ToolBar from './Components/ToolBar/ToolBar';
import DesignerView from "./Components/Designer/DesignerView";
import CanvasContainer from "./Components/Canvas/CanvasContainer";
import LoadingScreen from "./Components/LoadingScreen/LoadingScreen";


function App() {
  // --- State Variables ---
  const [selectedTool, setSelectedTool] = useState("Panel");
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedNomexColor, setSelectedNomexColor] = useState(null);
  const [isRotating, setIsRotating] = useState(false);
  const [canvasState, setCanvasState] = useState(null);
  const [envelopesData, setEnvelopesData] = useState(null);
  const [fabricsData, setFabricsData] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedModelUrl, setSelectedModelUrl] = useState(null);
  const [selectedModelGrid, setSelectedModelGrid] = useState(null);
  const [selectedFabric, setSelectedFabric] = useState(null);
  const [showContextToolBar, setShowContextToolBar] = useState(true);
  const [scenes, setScenes] = useState([])
  const [selectedScene, setSelectedScene] = useState(null);
  const [viewMode, setViewMode] = useState("Split");
  const [history, setHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [envelopeDimension, setEnvelopeDimension] = useState(null);
  const [settings, setSettings] = useState(null);
  const [modelLoaded, setModelLoaded] = useState(false);
  const [paraventSize, setParaventSize] = useState(null);
  const [initialFetchComplete, setInitialFetchComplete] = useState(false);
  const [envelopeConfirmed, setEnvelopeConfirmed] = useState(false);
  const canvasRef = useRef(null);
  const designerRef = useRef(null);
  const isDevelopment = process.env.NODE_ENV !== 'production';

  const BACKEND_BASE_URL = isDevelopment
    ? '/kreatura/designer-api'
    : 'https://kubicekballoons.com/en/designer-api';


  // Function to load an image as a base64 data URL
  const loadImageAsBase64 = (imageUrl) => {
    return fetch(imageUrl)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.statusText}`);
        }
        return response.blob();
      })
      .then(blob => {
        if (imageUrl.toLowerCase().endsWith('.svg') && blob.type !== 'image/svg+xml') {
          blob = new Blob([blob], { type: 'image/svg+xml' });
        }
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      });
  };


  // --- Data Fetching ---
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const id = urlParams.get('id');

    const fetchData = async () => {
      try {
        const fetchPromises = [
          fetch(`${BACKEND_BASE_URL}/envelope`),
          fetch(`${BACKEND_BASE_URL}/fabrics`),
          fetch(`${BACKEND_BASE_URL}/settings`),
          fetch(`${BACKEND_BASE_URL}/scenes`)
        ];


        if (id) {
          fetchPromises.push(fetch(`${BACKEND_BASE_URL}/design/${id}`));
        }

        const responses = await Promise.all(fetchPromises);

        if (responses.some(response => !response.ok)) {
          throw new Error('Failed to fetch data');
        }

        const [envelopes, fabrics, settings, scenes, design] = await Promise.all(responses.map(response => response.json()));

        setEnvelopesData(envelopes);
        setFabricsData(fabrics);
        setScenes(scenes.items);
        setSelectedScene(scenes.items.find(scene => scene.id === scenes.default));
        setSettings(settings);

        if (design) {
          let selectedFabric = fabrics.items.find((fabric) => fabric.id === design.design.selectedFabricID)
          const hexToIdMap = {};
          selectedFabric.colors.items.forEach(color => {
            hexToIdMap[color.id] = color.hex;
          });

          const nomexHexToIdMap = {};
          selectedFabric.nomex.items.forEach(color => {
            nomexHexToIdMap[color.id] = color.hex;
          });


          let canvasState = JSON.parse(design.design.canvasState)
          // Reconstruct optimized state (resolve internal references)
          canvasState = reconstructCanvasState(canvasState);

          const nomexRowsCount = canvasState.nomexBottomRows || 1;

          const modifiedEnvelopeGrid = canvasState.envelopeGrid.map((row, idx) => {
            if (idx < nomexRowsCount) {
              return row.map(id => {
                const targetId = (!id || id === "") ? canvasState.nomexColor : id;
                return nomexHexToIdMap[targetId] || targetId;
              });
            } else {
              return row.map(id => hexToIdMap[id] || id)
            }
          }
          );

          const imagePromises = canvasState.images.map(async (image) => {
            try {
              // If it's a data URL (from optimized state), use it directly
              if (image.src && image.src.startsWith('data:')) {
                return image;
              }
              // Otherwise (legacy path), load from server
              const base64Data = await loadImageAsBase64(settings.filesBaseUrl + image.src);
              return { ...image, src: base64Data };
            } catch (error) {
              console.error(`Error loading image ${image.src}:`, error);
              return image;
            }
          });

          const paraventPromise = async () => {
            try {
              const base64Data = await loadImageAsBase64(settings.filesBaseUrl + canvasState.paraventLogo.src);
              return { ...canvasState.paraventLogo, src: base64Data };
            } catch (error) {
              console.error(`Error loading paravent ${canvasState.paraventLogo.src}:`, error);
              return canvasState.paraventLogo;
            }
          };
          let updatedImages = [];
          let updatedParaventLogo = null;
          if (canvasState.images) {
            updatedImages = await Promise.all(imagePromises);
          }
          if (canvasState.paraventLogo) {
            updatedParaventLogo = await paraventPromise();
          }

          setSelectedModel(envelopes.items.find((envelope) => envelope.id === design.design.selectedModelID));
          setSelectedFabric(selectedFabric);
          setCanvasState({ ...canvasState, envelopeGrid: modifiedEnvelopeGrid, images: updatedImages, paraventLogo: updatedParaventLogo, loadlines: canvasState.loadlines || '#FFFFFF' });
          setSelectedNomexColor(canvasState.nomexColor);
          const defaultColor = selectedFabric.colors.items.find(
            color => color.id === selectedFabric.colors.defaultPaint
          );
          if (!selectedColor) {
            setSelectedColor(defaultColor);
          }
          setEnvelopeConfirmed(true);
        } else {
          let selectedFabric = fabrics.items.find((fabric) => fabric.id === fabrics.default)
          setSelectedModel(envelopes.items.find((envelope) => envelope.id === envelopes.default))
          setSelectedFabric(selectedFabric)

          const defaultColor = selectedFabric.colors.items.find(
            color => color.id === selectedFabric.colors.defaultPaint
          );
          const defaultNomexColor = selectedFabric.nomex.items.find(
            color => color.id === selectedFabric.nomex.defaultPaint
          );

          if (!selectedColor) {
            setSelectedColor(defaultColor);
          }
          if (!selectedNomexColor) {
            setSelectedNomexColor(defaultNomexColor);
          }
        }
        setInitialFetchComplete(true);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();

  }, []);


  // --- Global Drag & Drop Prevention ---
  useEffect(() => {
    const handleGlobalDragOver = (e) => {
      e.preventDefault();
    };

    const handleGlobalDrop = (e) => {
      e.preventDefault();
    };

    window.addEventListener('dragover', handleGlobalDragOver);
    window.addEventListener('drop', handleGlobalDrop);

    return () => {
      window.removeEventListener('dragover', handleGlobalDragOver);
      window.removeEventListener('drop', handleGlobalDrop);
    };
  }, []);


  function flattenSVG(svgString) {
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgString.trim(), "image/svg+xml");
    const newSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    const originalSvg = svgDoc.querySelector('svg');
    for (let i = 0; i < originalSvg.attributes.length; i++) {
      newSvg.setAttribute(originalSvg.attributes[i].name, originalSvg.attributes[i].value);
    }

    svgDoc.querySelectorAll('g').forEach(group => {
      if (group.id.substring(0, 3) === "pol") {
        Array.from(group.children).forEach(element => {
          const secondThirdChars = element.id.substring(1, 3);
          const polNumber = group.id.substring(3)
          element.setAttribute('id', secondThirdChars + "-" + polNumber)
          element.removeAttribute('style')
          newSvg.appendChild(element);
        });
      }
    });
    const serializer = new XMLSerializer();
    const modifiedSVGString = serializer.serializeToString(newSvg);

    return modifiedSVGString;
  }

  useEffect(() => {
    if (selectedModel) {
      const fetchData = async () => {
        try {
          const response = await fetch(BACKEND_BASE_URL + selectedModel.data.split('api')[1]);
          if (!response.ok) {
            throw new Error('Failed to fetch data');
          }
          const data = await response.json();
          let svgPath = data.resources.svgPath;
          fetch(svgPath).then(response => response.text()).then(
            svgText => {
              setSelectedModelGrid(parse(flattenSVG(svgText)).children[0]);
            }
          )
          setSelectedModelUrl(data.resources.modelPath)
          let paraventSize = data.resources.paraventSize;
          setParaventSize(paraventSize);
        } catch (error) {
          console.error('Error fetching data:', error);
        }
      }
      fetchData();
    }
  }, [selectedModel]);


  const doReset = (model = selectedModel) => {
    setCanvasState(null);
    setHistory([]);
    if (model !== selectedModel) {
      setSelectedModel(model)
    } else {
      setSelectedModelGrid({ ...selectedModelGrid });
    }
    if (selectedFabric) {
      const defaultColor = selectedFabric.colors.items.find(
        color => color.id === selectedFabric.colors.defaultPaint
      );
      setSelectedColor(defaultColor);
      const defaultNomexColor = selectedFabric.nomex.items.find(
        color => color.id === selectedFabric.nomex.defaultPaint
      );
      setSelectedNomexColor(defaultNomexColor);
    }
  }

  const handleSetFabric = (newFabric) => {
    if (!selectedFabric || !canvasState || newFabric.id === selectedFabric.id) {
      setSelectedFabric(newFabric);
      return;
    }

    const nomexRowsCount = canvasState.nomexBottomRows || 1;
    const remappedGrid = remapEnvelopeGridColors(
      canvasState.envelopeGrid, nomexRowsCount, selectedFabric, newFabric
    );

    const changes = [];
    canvasState.envelopeGrid.forEach((row, rowIdx) => {
      row.forEach((oldHex, colIdx) => {
        const newHex = remappedGrid[rowIdx][colIdx];
        if (newHex !== oldHex) {
          changes.push({ path: 'envelopeGrid', row: rowIdx, col: colIdx, old: oldHex, new: newHex });
        }
      });
    });
    changes.push({ path: 'selectedFabric', old: selectedFabric, new: newFabric });

    setCanvasState(prev => ({ ...prev, envelopeGrid: remappedGrid }));

    if (selectedColor) {
      const newColorId = resolveSubstituteColorId(selectedFabric, selectedColor.id, newFabric);
      const newColor = newFabric.colors.items.find(color => color.id === newColorId)
        ?? newFabric.colors.items.find(color => color.id === newFabric.colors.defaultPaint);
      changes.push({ path: 'selectedColor', old: selectedColor, new: newColor });
      setSelectedColor(newColor);
    }

    setSelectedFabric(newFabric);

    setHistory(prev => [...prev, changes].slice(-100)); // mirror Canvas.jsx's historyLimit
    setRedoStack([]);
  };

  const undo = () => {
    const lastStep = history.at(-1);
    const fabricChange = lastStep?.find(c => c.path === 'selectedFabric');
    if (fabricChange) {
      setSelectedFabric(fabricChange.old);
    }
    const colorChange = lastStep?.find(c => c.path === 'selectedColor');
    if (colorChange) {
      setSelectedColor(colorChange.old);
    }
    canvasRef.current.undo();
  };

  const redo = () => {
    const nextStep = redoStack.at(-1);
    const fabricChange = nextStep?.find(c => c.path === 'selectedFabric');
    if (fabricChange) {
      setSelectedFabric(fabricChange.new);
    }
    const colorChange = nextStep?.find(c => c.path === 'selectedColor');
    if (colorChange) {
      setSelectedColor(colorChange.new);
    }
    canvasRef.current.redo();
  };


  // --- JSX Rendering ---
  return (
    <div className="flex flex-col items-center w-full h-full bg-[var(--k-100-f0f0f0)] font-[var(--unnamed-font-family-nexa)]">
      {initialFetchComplete &&
        <>
          {envelopesData && fabricsData &&
            <TopBar
              modelOptions={envelopesData}
              fabricOptions={fabricsData}
              selectedFabric={selectedFabric}
              selectedModel={selectedModel}
              onSetModel={(newModel) => {
                doReset(newModel);
              }}
              onSetFabric={handleSetFabric}
              onReset={doReset}
              envelopeConfirmed={envelopeConfirmed}
              setEnvelopeConfirmed={setEnvelopeConfirmed}
              canvasState={canvasState}
              settings={settings}
              onUndo={undo}
              onRedo={redo}
              history={history}
              redoStack={redoStack}
              viewMode={viewMode}
              setViewMode={setViewMode}
              scenes={scenes}
              selectedScene={selectedScene}
              setSelectedScene={setSelectedScene}
              designerRef={designerRef}
              canvasRef={canvasRef}
              isRotating={isRotating}
              setIsRotating={setIsRotating}
            />}
          <div style={{ display: 'flex', width: '100%', height: '100%', flexDirection: 'column', justifyContent: 'end', alignItems: 'center', overflow: 'hidden' }}>
            {!modelLoaded && <LoadingScreen />}

            {canvasState && selectedScene && (
              <DesignerView
                modelUrl={selectedModelUrl}
                canvasState={canvasState}
                viewMode={viewMode}
                selectedScene={selectedScene}
                selectedTool={selectedTool}
                isRotating={isRotating}
                setIsRotating={setIsRotating}
                envelopeDimension={envelopeDimension}
                settings={settings}
                setModelLoaded={setModelLoaded}
                ref={designerRef}
              />
            )}

            {selectedModelGrid && selectedFabric && paraventSize &&
              <CanvasContainer
                svgContent={selectedModelGrid}
                selectedTool={selectedTool}
                selectedColor={selectedColor}
                setSelectedColor={setSelectedColor}
                selectedNomexColor={selectedNomexColor}
                setSelectedNomexColor={setSelectedNomexColor}
                showContextToolBar={showContextToolBar}
                setShowContextToolBar={setShowContextToolBar}
                setSelectedTool={setSelectedTool}
                canvasState={canvasState}
                setEnvelopeDimension={setEnvelopeDimension}
                setCanvasState={setCanvasState}
                defaultColor={selectedFabric.colors.items.find(
                  color => color.id === selectedFabric.colors.default
                )}
                defaultNomexColor={selectedFabric.nomex.items.find(
                  color => color.id === selectedFabric.nomex.default
                )}
                history={history}
                setHistory={setHistory}
                redoStack={redoStack}
                setRedoStack={setRedoStack}
                canvasRef={canvasRef}
                viewMode={viewMode}
                scenes={scenes}
                selectedScene={selectedScene}
                setSelectedScene={setSelectedScene}
                selectedFabric={selectedFabric}
                paraventSize={paraventSize}
              />}
          </div>
          {selectedColor && viewMode !== "Envelope" && (
            <ToolBar
              selectedTool={selectedTool}
              selectedColor={selectedColor}
              showContextToolBar={showContextToolBar}
              onShowContextToolBar={setShowContextToolBar}
              onToolChange={(newTool) => {
                setShowContextToolBar(true);
                setSelectedTool(newTool);
              }}
            />
          )}
        </>}

    </div>
  );

}

export default App;
